import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { readConsent, writeConsent, type ConsentChoice, type ConsentRecord } from './consentStore';
import { applyAnalyticsConsent } from '../telemetry/analyticsConsent';
import { logEvent } from '../telemetry/logEvent';
import { runtimeEnv } from '../../runtimeEnv';

// The app's first real React context — consent is genuinely cross-cutting (banner + footer
// share this state). Owns the decision only; the start/stop side effects live in the seam.
type ConsentValue = {
  choice: ConsentChoice | null;
  needsBanner: boolean;
  accept: () => void;
  reject: () => void;
  withdraw: () => void;
  reopen: () => void;
};

type ProviderDeps = {
  read: () => ConsentRecord | null;
  write: (c: ConsentChoice) => ConsentRecord;
  apply: (c: ConsentChoice) => void;
  log: (name: string, body: string, fields?: Record<string, unknown>) => void;
};

const defaultDeps: ProviderDeps = {
  read: () => readConsent(),
  write: (c) => writeConsent(c),
  apply: applyAnalyticsConsent,
  log: (name, body, fields) => logEvent(name, body, fields),
};

const ConsentContext = createContext<ConsentValue | null>(null);

export function useConsent(): ConsentValue {
  const ctx = useContext(ConsentContext);
  if (!ctx) throw new Error('useConsent must be used within a ConsentProvider');
  return ctx;
}

// For chrome that may render outside the provider (page tests); null means "no consent UI here".
export function useConsentIfAvailable(): ConsentValue | null {
  return useContext(ConsentContext);
}

export function ConsentProvider({ children, deps = defaultDeps }: Readonly<{ children: ReactNode; deps?: ProviderDeps }>) {
  const { read, write, apply, log } = deps;
  const [choice, setChoice] = useState<ConsentChoice | null>(() => read()?.choice ?? null);
  // Set by the footer's "Cookie preferences": shows the banner again until the next decision.
  const [reopened, setReopened] = useState(false);

  const set = useCallback((c: ConsentChoice) => {
    write(c);
    // The decision stands even if a tracker throws while starting or stopping; otherwise the
    // banner would stay up with a choice already stored.
    try {
      apply(c);
    } catch (e) {
      log('consent.apply_failed', 'analytics start/stop threw', { choice: c, error: e instanceof Error ? e.message : String(e) });
    }
    log('consent.decision', 'analytics consent set', { choice: c, policyVersion: runtimeEnv.consentPolicyVersion });
    setChoice(c);
    setReopened(false);
  }, [write, apply, log]);

  const accept = useCallback(() => set('granted'), [set]);
  const reject = useCallback(() => set('denied'), [set]);
  const withdraw = useCallback(() => set('denied'), [set]);
  const reopen = useCallback(() => setReopened(true), []);

  // Honour a consent change made in another tab. A null key is a whole-storage clear. A record
  // that has gone means no consent, so anything running is stopped.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== null && e.key !== 'eh_cookie_consent') return;
      const next = read()?.choice ?? null;
      setChoice(next);
      apply(next ?? 'denied');
    };
    globalThis.addEventListener('storage', onStorage);
    return () => globalThis.removeEventListener('storage', onStorage);
  }, [read, apply]);

  const value = useMemo<ConsentValue>(
    () => ({ choice, needsBanner: choice === null || reopened, accept, reject, withdraw, reopen }),
    [choice, reopened, accept, reject, withdraw, reopen],
  );
  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
}
